import ObjectID from 'bson-objectid'
import { Collection } from 'mongodb'

import { tablesHeaders } from './db'
import {getCurrentDocuments, resolveDocumentsAccessToken } from '../api/documents.api'
import { getCurrentCustomers } from '../api/customers.api'
import { resolveCustomersAccessToken } from '../sso/services/customers-access.service'
import { systemUsersHealthCheck } from '../api/system-users.api'
import { authApiHealthCheck } from '../api/auth.api'
import { Customer, TablesHeaders, SystemUser, UserSessionType } from '../types'
import { normalizeCustomerData, normalizeSystemUserData } from './normalize-data.utils'
import { handleCheckIfDatabaseExists, handleCreateDatabase, handleDeleteDataByOne, handleGetDataFromTableById, handleSetDataToTable, handleUpdateDataByOne } from './db/common-methods'

export const handleGetAuthApiHealthCheck = (): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const healthAuthCheckResponse = await authApiHealthCheck()
      resolve(healthAuthCheckResponse)
    } catch (error) {
      console.error('Error handleGetAuthApiHealthCheck', error)
      reject(false)
    }
  })
}

export const handleGetSystemUsersApiHealthCheck = (): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const healthSystemUsersCheckResponse = await systemUsersHealthCheck()
      resolve(healthSystemUsersCheckResponse)
    } catch (error) {
      console.error('Error handleGetSystemUsersApiHealthCheck', error)
      reject(false)
    }
  })
}

export const handleSyncDocumentsData = (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const refreshToken = window.sessionStorage.getItem('token')
    const userSessionData = window.sessionStorage.getItem('userSession') || window.localStorage.getItem('userSession')

    if (userSessionData) {
      const accessToken = await resolveDocumentsAccessToken(refreshToken ? JSON.parse(refreshToken) : null)
      const currentDocuments = await getCurrentDocuments(accessToken)

      try {
        const databaseExists = await handleCheckIfDatabaseExists()

        if (databaseExists) {
          await handleSetDataToTable('documents', tablesHeaders['documents'].join(','), currentDocuments.data)
        } else {
          const databaseTablesStructure = handleGetDatabaseTablesStructure()
          await handleCreateDatabase(databaseTablesStructure, 'documents', tablesHeaders['documents'].join(','), currentDocuments.data)
          console.log('Database created with success')
        }
      } catch (error) {
        console.error('Error handleSyncDocumentsData', error)
      }

      resolve(true)
    } else {
      reject(false)
    }
  })
}

export const handleSyncCustomersData = (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    const refreshToken = window.sessionStorage.getItem('token')
    const userSessionData = window.sessionStorage.getItem('userSession') || window.localStorage.getItem('userSession')

    if (refreshToken && userSessionData) {
      const accessToken = await resolveCustomersAccessToken(refreshToken ? JSON.parse(refreshToken) : null)
      const currentCustomers = await getCurrentCustomers(accessToken)

      if (
        currentCustomers.data !== null &&
        currentCustomers.data.length > 0 &&
        typeof currentCustomers.data === 'object' &&
        Object.keys(currentCustomers.data[0]).length > 0 &&
        Object.keys(currentCustomers.data[0]).includes('_id') &&
        currentCustomers.data[0]._id !== ''
      ) {
        try {
          const databaseExists = await handleCheckIfDatabaseExists()

          if (databaseExists) {
            await handleSetDataToTable('customers', tablesHeaders['customers'].join(','), currentCustomers.data)
          } else {
            const databaseTablesStructure = handleGetDatabaseTablesStructure()
            await handleCreateDatabase(databaseTablesStructure, 'customers', tablesHeaders['customers'].join(','), currentCustomers.data)
            console.log('Database created with success')
          }
        } catch (error) {
          console.error('Error handleSyncCustomersData', error)
        }
      }

      resolve(true)
    } else {
      reject(false)
    }
  })
}

const handleGetDatabaseTablesStructure = () => {
  const databaseTablesStructure: any = {}
  const tablesNames = Object.keys(tablesHeaders)
  const indexTableToDelete = tablesNames.indexOf('__dexie-encrypted-addon__')

  if (indexTableToDelete > -1) {
    tablesNames.splice(indexTableToDelete, 1)
  }

  for (let i = 0; i < tablesNames.length; i++) {
    databaseTablesStructure[tablesNames[i]] = (tablesHeaders as any)[tablesNames[i]].join(',').replaceAll(' ', '')
  }

  return databaseTablesStructure
}

const handleNormalizeData = (tableName: string, dataToNormalize: any) => {
  if (tableName === 'system-users') {
    return normalizeSystemUserData(dataToNormalize)
  } else if (tableName === 'customers') {
    return normalizeCustomerData(dataToNormalize)
  }
}

export const handleGetCustomerDataByRegex = async (customersCollection: Collection | null, stringToSearch: string, currentCustomersIds: Array<ObjectID>, belongsTo: string) => {
  const customerFindingsByFullName = await customersCollection?.find({
    belongsTo,
    deleted: false,
    // @ts-expect-error TS2769
    _id: { $in: currentCustomersIds },
    registeredFullName: {
      $regex: new RegExp(`${stringToSearch.toLowerCase()}`, 'i')
    }
  })

  return customerFindingsByFullName
}

export const handleGetDataFromAvailableResourceById = async (tableName: string, collectionToSearch: Collection | null, idToSearch: string) => {
  try {
    const dataFromLocalData: Array<SystemUser | Customer> = (await handleGetDataFromTableById(tableName, String(idToSearch))) as Array<SystemUser | Customer>

    if (dataFromLocalData.length === 0 && collectionToSearch) {
      // @ts-expect-error TS2769
      const dataFromRemoteDb = await collectionToSearch.findOne({ _id: ObjectID(idToSearch) })

      if (!dataFromRemoteDb) {
        return null
      }

      const nomalizeData = handleNormalizeData(tableName, dataFromRemoteDb) as any
      await handleSyncDataToLocalDB(tableName, nomalizeData)

      return nomalizeData
    } else {
      return dataFromLocalData[0]
    }
  } catch (error) {
    console.log('handleGetDataFromAvailableResourceById > ', error)
  }
}

export const handleSyncDataToLocalDB = async (tableName: string, dataToSync: SystemUser | Partial<SystemUser>) => {
  const dataSetted: Array<SystemUser> = (await handleGetDataFromTableById(tableName, String(dataToSync._id))) as Array<SystemUser>

  if (dataSetted.length === 0) {
    await handleSetDataToTable(tableName, (tablesHeaders as TablesHeaders)[`${tableName}` as keyof TablesHeaders]?.join(',') as string, dataToSync)
  } else {
    await handleUpdateDataByOne(tableName, String(dataToSync._id), dataToSync)
  }
}

export const handleCheckIfIsAllowedToUpdatePaymentDetailsData = (collectionDbEvents: Array<any>, userSession: UserSessionType, paymentId: string) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if ((userSession.role === 'admin' || userSession.role === 'super-admin' || userSession.role === 'admin-builder') && fullDocument && fullDocument.belongsTo === 'holdings' && String(fullDocument._id) === paymentId) {
    return true
  }

  if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo && String(fullDocument._id) === paymentId) {
    return true
  }

  if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && fullDocument && fullDocument.belongsTo === userSession.belongsTo && String(fullDocument._id) === paymentId) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.fundsFromPocket)

    if (registryBelongsToSystemUser) {
      return true
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdatePaymentsData = (collectionDbEvents: Array<any>, userSession: UserSessionType, documentId?: string) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if (documentId) {
    if (
      (userSession.role === 'admin' || userSession.role === 'super-admin' || userSession.role === 'admin-builder') &&
      fullDocument &&
      fullDocument.belongsTo === 'holdings' &&
      String(fullDocument.documentId) === documentId
    ) {
      return true
    }

    if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo && String(fullDocument.documentId) === documentId) {
      return true
    }

    if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && fullDocument && fullDocument.belongsTo === userSession.belongsTo && String(fullDocument.documentId) === documentId) {
      const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
      const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.fundsFromPocket)

      if (registryBelongsToSystemUser) {
        return true
      }
    }
  } else {
    if ((userSession.role === 'admin' || userSession.role === 'super-admin' || userSession.role === 'admin-builder') && fullDocument && fullDocument.belongsTo === 'holdings') {
      return true
    }

    if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
      return true
    }

    if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
      const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
      const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.fundsFromPocket)

      if (registryBelongsToSystemUser) {
        return true
      }
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdateCustomersData = (collectionDbEvents: Array<any>, userSession: UserSessionType) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if ((userSession.role === 'admin' || userSession.role === 'super-admin' || userSession.role === 'admin-builder') && fullDocument && fullDocument.belongsTo === 'holdings') {
    return true
  }

  if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    return true
  }

  if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryCustomerCompanyPocketsRelated = fullDocument.customerCompanyPocketsRelated
    const registryBelongsToSystemUser = registryCustomerCompanyPocketsRelated.some((pocketId: string) => userSessionAdminOfPockets.includes(pocketId))

    if (registryBelongsToSystemUser) {
      return true
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdateDocumentsToReviewData = (collectionDbEvents: Array<any>, userSession: UserSessionType) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if (
    (userSession?.role === 'admin' || userSession?.role === 'super-admin') &&
    fullDocument &&
    fullDocument.belongsTo === 'holdings' &&
    (fullDocument.status === 'stopped' ||
      fullDocument.status === 'uploaded' ||
      fullDocument.status === 'downloaded' ||
      fullDocument.status === 'deleted' ||
      fullDocument.status === 'analysis-completed' ||
      fullDocument.status === 'error-analyzing-file' ||
      fullDocument.status === 'error-downloading-file' ||
      fullDocument.status === 'downloaded-n-analyzing')
  ) {
    return true
  }

  if (
    userSession?.role === 'admin-builder' &&
    fullDocument &&
    fullDocument.belongsTo === 'holdings' &&
    fullDocument.uploadedBy === userSession?._id &&
    (fullDocument.status === 'stopped' ||
      fullDocument.status === 'uploaded' ||
      fullDocument.status === 'downloaded' ||
      fullDocument.status === 'deleted' ||
      fullDocument.status === 'analysis-completed' ||
      fullDocument.status === 'error-analyzing-file' ||
      fullDocument.status === 'error-downloading-file' ||
      fullDocument.status === 'downloaded-n-analyzing')
  ) {
    return true
  }

  if (
    userSession?.role === 'super-admin-client' &&
    fullDocument &&
    fullDocument.belongsTo === userSession.belongsTo &&
    (fullDocument.status === 'stopped' ||
      fullDocument.status === 'uploaded' ||
      fullDocument.status === 'downloaded' ||
      fullDocument.status === 'deleted' ||
      fullDocument.status === 'analysis-completed' ||
      fullDocument.status === 'error-analyzing-file' ||
      fullDocument.status === 'error-downloading-file' ||
      fullDocument.status === 'downloaded-n-analyzing')
  ) {
    return true
  }

  if (
    (userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder') &&
    fullDocument &&
    fullDocument.belongsTo === userSession.belongsTo &&
    (fullDocument.status === 'stopped' ||
      fullDocument.status === 'uploaded' ||
      fullDocument.status === 'downloaded' ||
      fullDocument.status === 'deleted' ||
      fullDocument.status === 'analysis-completed' ||
      fullDocument.status === 'error-analyzing-file' ||
      fullDocument.status === 'error-downloading-file' ||
      fullDocument.status === 'downloaded-n-analyzing')
  ) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.customerCompanyPocketUsed)

    if (registryBelongsToSystemUser && userSession?.role === 'admin-client') {
      return true
    }

    if (registryBelongsToSystemUser && userSession?.role === 'admin-client-builder' && fullDocument.uploadedBy === String(userSession._id)) {
      return true
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdateBatchesData = (collectionDbEvents: Array<any>, userSession: UserSessionType) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    return true
  }

  if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.customerCompanyPocketUsed)

    if (registryBelongsToSystemUser && userSession?.role === 'admin-client') {
      return true
    }

    if (registryBelongsToSystemUser && userSession?.role === 'admin-client-builder' && fullDocument.uploadedBy === String(userSession._id)) {
      return true
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdateDocumentsDataFromCustomers = (collectionDbEvents: Array<any>, userSession: UserSessionType) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if ((userSession.role === 'admin' || userSession.role === 'super-admin' || userSession.role === 'admin-builder') && fullDocument && fullDocument.belongsTo === 'holdings') {
    return true
  }

  if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    return true
  }

  if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.customerCompanyPocketUsed)

    if (registryBelongsToSystemUser && userSession?.role === 'admin-client') {
      return true
    }

    if (registryBelongsToSystemUser && userSession?.role === 'admin-client-builder' && fullDocument.uploadedBy === String(userSession._id)) {
      return true
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdateDocumentsData = (collectionDbEvents: Array<any>, userSession: UserSessionType) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if ((userSession.role === 'admin' || userSession.role === 'super-admin') && fullDocument && fullDocument.belongsTo === 'holdings') {
    return true
  }

  if (userSession.role === 'admin-builder' && fullDocument && fullDocument.belongsTo === 'holdings' && fullDocument.uploadedBy === String(userSession._id)) {
    return true
  }

  if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    return true
  }

  if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && fullDocument && fullDocument.belongsTo === userSession.belongsTo) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.customerCompanyPocketUsed)

    if (registryBelongsToSystemUser && userSession.role === 'admin-client') {
      return true
    }

    if (registryBelongsToSystemUser && userSession.role === 'admin-client-builder' && fullDocument.uploadedBy === String(userSession._id)) {
      return true
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdateDocumentDetailsData = (collectionDbEvents: Array<any>, documentId: string, userSession: UserSessionType) => {
  const fullDocument = collectionDbEvents[0].fullDocument

  if ((userSession.role === 'admin' || userSession.role === 'super-admin') && fullDocument && fullDocument.belongsTo === 'holdings' && String(fullDocument._id) === documentId) {
    return true
  }

  if (userSession.role === 'admin-builder' && fullDocument && fullDocument.belongsTo === 'holdings' && fullDocument.uploadedBy === String(userSession._id) && String(fullDocument._id) === documentId) {
    return true
  }

  if (userSession.role === 'super-admin-client' && fullDocument && fullDocument.belongsTo === userSession.belongsTo && String(fullDocument._id) === documentId) {
    return true
  }

  if ((userSession.role === 'admin-client-builder' || userSession.role === 'admin-client') && fullDocument && fullDocument.belongsTo === userSession.belongsTo && String(fullDocument._id) === documentId) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryBelongsToSystemUser = userSessionAdminOfPockets.includes(fullDocument.customerCompanyPocketUsed)

    if (registryBelongsToSystemUser && userSession.role === 'admin-client') {
      return true
    }

    if (registryBelongsToSystemUser && userSession.role === 'admin-client-builder' && fullDocument.uploadedBy === String(userSession._id)) {
      return true
    }
  }

  if (!fullDocument && collectionDbEvents[0].operationType === 'delete') {
    return true
  }

  return false
}

export const handleCheckIfIsAllowedToUpdateSystemUsersData = async (collectionDbEvents: Array<any>, userSession: UserSessionType) => {
  if (
    (userSession.role === 'admin' || userSession.role === 'super-admin') &&
    collectionDbEvents[0].fullDocument &&
    collectionDbEvents[0].fullDocument.role !== 'admin-client-builder' &&
    collectionDbEvents[0].fullDocument.role !== 'admin-client'
  ) {
    return true
  }

  if ((userSession.role === 'admin' || userSession.role === 'super-admin') && !collectionDbEvents[0].fullDocument && collectionDbEvents[0].operationType === 'delete') {
    const documentKey = String(collectionDbEvents[0].documentKey._id)
    const systemUserDataSetted: Array<SystemUser> = (await handleGetDataFromTableById('system-users', String(documentKey))) as Array<SystemUser>

    if (systemUserDataSetted.length > 0) {
      await handleDeleteDataByOne('system-users', documentKey)
    }

    return true
  }

  if (userSession.role === 'super-admin-client' && collectionDbEvents[0].fullDocument && collectionDbEvents[0].fullDocument.belongsTo === userSession.belongsTo) {
    return true
  }

  if ((userSession.role === 'admin-client' || userSession.role === 'admin-client-builder') && collectionDbEvents[0].fullDocument && collectionDbEvents[0].fullDocument.belongsTo === userSession.belongsTo) {
    const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []
    const registryAdminOfPockets = collectionDbEvents[0].fullDocument.adminOfPockets
    const registryBelongsToSystemUser = registryAdminOfPockets.some((pocketId: string) => userSessionAdminOfPockets.includes(pocketId))

    if (registryBelongsToSystemUser) {
      return true
    }
  }

  if (userSession.role.indexOf('-client') > -1 && !collectionDbEvents[0].fullDocument && collectionDbEvents[0].operationType === 'delete') {
    const documentKey = String(collectionDbEvents[0].documentKey._id)
    const systemUserDataSetted: Array<SystemUser> = (await handleGetDataFromTableById('system-users', String(documentKey))) as Array<SystemUser>

    if (systemUserDataSetted.length > 0) {
      await handleDeleteDataByOne('system-users', documentKey)
    }

    return true
  }

  return false
}
